import { Observable } from 'rxjs';
import { push } from 'react-router-redux';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';
import _ from 'lodash';
import { ActionsObservable } from 'redux-observable';
import { Store } from 'redux';

import Volunteer from 'entities/volunteers/model';
import VolunteersApi from 'entities/volunteers/api';
import LocationsApi from 'entities/location/api';
import { selectVolunteerById } from 'entities/volunteers/selectors';
import { selectLocations } from 'entities/location/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';

import {
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  initAction,
  saveAction,
} from './actions';
import { INIT, FORM_IDENTIFIER, SAVE } from './constants';

const init = (action$: ActionsObservable<ReturnType<typeof initAction>>, store: Store) =>
  action$.ofType(INIT).exhaustMap((payload) => {
    const actions = [];
    const state = store.getState();

    const initializeForm = (volunteer: Volunteer) =>
      initialize(FORM_IDENTIFIER, {
        firstName: volunteer.fullName.first,
        lastName: volunteer.fullName.last,
        email: volunteer.email,
        phoneNumber: volunteer.phone,
        department: volunteer.organizationDepartment,
        location: volunteer.location,
        locale: volunteer.locale,
        tShirtSize: volunteer.tShirtSize,
      });

    const volunteer = selectVolunteerById(state, payload.id);
    if (volunteer) {
      actions.push(Observable.of(initializeForm(volunteer)));
    } else {
      actions.push(
        VolunteersApi.fetchById(payload.id).map((action) => [
          action,
          initializeForm(action.volunteer),
        ])
      );
    }

    const locations = selectLocations(state);
    if (locations.size === 0) {
      actions.push(LocationsApi.fetchAll());
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$: ActionsObservable<ReturnType<typeof saveAction>>) =>
  action$.ofType(SAVE).mergeMap((payload) => {
    const data = { ...payload.data };

    return Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.from(
        VolunteersApi.update(payload.data.id, {
          fullName: {
            first: data.firstName,
            last: data.lastName,
          },
          email: data.email,
          phone: data.phoneNumber,
          organizationDepartment: data.department,
          location: data.location,
          locale: data.locale,
          tShirtSize: data.tShirtSize,
        })
      )
        .mergeMap((action) => [
          action,
          saveSuccessAction(),
          stopSubmit(FORM_IDENTIFIER),
          showMessageAction('Changes Saved!'),
          push(`/volunteers/${payload.data.id}`),
        ])
        .catch((e) =>
          Observable.of(
            saveFailedAction(e, 'saveFailed'),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    );
  });

export default [init, save];
